import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    AppointmentsCalendarView,
    getTodayIso,
    type AppointmentTab,
    type AppointmentsCalendarComposer
} from '@odo/shared-ui';
import { Avatar } from '../../Avatar';
import { PracticeDoctorLabel } from '../../shared/PracticeDoctorLabel';
import { AppointmentDetailPane } from './AppointmentDetailPane';
import { AppointmentsModalFrame } from './AppointmentsModalFrame';
import { AppointmentStatusChip } from './AppointmentStatusChip';
import { PracticeFilterList, type CalendarPractice } from './PracticeFilterList';
import {
    useAdminAppointmentDetail,
    useAdminAppointments
} from '../../../hooks/useAdminAppointments';
import { useToast } from '../../../providers/ToastProvider';
import type { AdminAppointment } from '../../../services/adminAppointments';
import { useEscapeKey } from '../../../hooks/useEscapeKey';

interface AdminAppointmentsModalProps {
    onClose: () => void;
    /** The practice / virtual switch rendered in the header. */
    viewToggle?: ReactNode;
}

const APPOINTMENT_QUERY_PARAM = 'appointment';
const PAST_RANGE_TABS: ReadonlySet<AppointmentTab> = new Set(['past', 'needsNote', 'all']);

export function AdminAppointmentsModal({ onClose, viewToggle }: AdminAppointmentsModalProps) {
    const todayIso = useMemo(() => getTodayIso(), []);
    const [includePast, setIncludePast] = useState(false);
    const { appointments, isLoading, isError, refetch, minMonth, maxMonth } =
        useAdminAppointments(includePast);
    const { showToast } = useToast();
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedId = searchParams.get(APPOINTMENT_QUERY_PARAM);
    const detail = useAdminAppointmentDetail(selectedId);

    const [search, setSearch] = useState('');
    const [practiceFilter, setPracticeFilter] = useState<CalendarPractice[]>([]);

    // The URL decides what is shown: the list row paints at once, the single GET adds the
    // origin event and covers links to rows outside the loaded range (whatever the sidebar filter).
    const selected: AdminAppointment | null =
        detail.data ?? appointments.find((a) => a.id === selectedId) ?? null;

    const filteredAppointments = useMemo(() => {
        if (practiceFilter.length === 0) return appointments;
        const allowed = new Set(practiceFilter.map((p) => p.id));
        return appointments.filter((a) => !!a.practiceId && allowed.has(a.practiceId));
    }, [appointments, practiceFilter]);

    // Pushes ?appointment=<id> so Back returns to the agenda.
    const openAppointmentParam = (id: string) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set(APPOINTMENT_QUERY_PARAM, id);
            return next;
        });
    };

    // Replaces (no history entry) - the single exit path for the detail pane's
    // close button, Escape, and the not-found fallback below.
    const closeAppointmentParam = () => {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.delete(APPOINTMENT_QUERY_PARAM);
                return next;
            },
            { replace: true }
        );
    };

    const handleTabChange = (tab: AppointmentTab) => {
        if (PAST_RANGE_TABS.has(tab)) setIncludePast(true);
    };

    useEffect(() => {
        if (!selectedId || selected || isLoading || !detail.isError) return;
        showToast('Appointment not found', 'error');
        closeAppointmentParam();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId, selected, isLoading, detail.isError]);

    // The detail view is the top layer: Escape backs out of it first, and only
    // closes the whole modal once nothing is selected.
    useEscapeKey(() => {
        if (selectedId) {
            closeAppointmentParam();
        } else {
            onClose();
        }
    }, true);

    const composer: AppointmentsCalendarComposer | undefined = selected
        ? {
              label: 'Appointment details',
              onClose: closeAppointmentParam,
              highlightDate: selected.date,
              content: (
                  <AppointmentDetailPane
                      appointment={selected}
                      originEvent={detail.data?.originEvent ?? null}
                  />
              )
          }
        : undefined;

    return (
        <AppointmentsModalFrame
            title="Appointments"
            viewToggle={viewToggle}
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by patient name"
            onClose={onClose}
        >
            <AppointmentsCalendarView
                appointments={filteredAppointments}
                isLoading={isLoading}
                isError={isError}
                onRetry={() => void refetch()}
                todayIso={todayIso}
                minMonth={minMonth}
                maxMonth={maxMonth}
                readOnly
                showPractice
                search={search}
                onSearchChange={setSearch}
                sidebarExtra={
                    <div className="pt-4 border-t border-gray-100">
                        <PracticeFilterList
                            selected={practiceFilter}
                            onChange={setPracticeFilter}
                        />
                    </div>
                }
                renderAvatar={(appointment) => (
                    <Avatar
                        user={{ name: appointment.patientName, email: appointment.patientId }}
                        imageURL={appointment.profilePictureUrl ?? undefined}
                    />
                )}
                renderPractice={(appointment) => (
                    <PracticeDoctorLabel
                        practiceId={appointment.practiceId}
                        practiceName={appointment.practiceName}
                        doctorName={appointment.practiceDoctorName}
                        format="doctor-practice"
                        className="block truncate"
                        toneClassName="text-gray-400"
                        tooltipSide="right"
                    />
                )}
                renderStage={(appointment) => (
                    <AppointmentStatusChip statusId={appointment.patientInternalStatus} />
                )}
                onSelectAppointment={(appointment) => openAppointmentParam(appointment.id)}
                onTabChange={handleTabChange}
                composer={composer}
            />
        </AppointmentsModalFrame>
    );
}
